/** Task-list projection. An occurrence is distinct from its repeating source node. */
import { dayKey } from '../core/recurrence.js';
import { MAX_ROUTINE_SECONDS } from '../core/routine-duration.js';
import { nextOccurrence, appendRoutineEvent, startRoutine, getRoutineState } from './routines.js';
import { USER_ORIGIN } from './origins.js';
import { t } from '../../shared/i18n/index.js';

export function routineListItems(state, noteId, includeDue = true, now = new Date()) {
  const removed = new Set(state.linkRecords.filter(l => l.noteId === noteId && l.removed).map(l => l.occurrenceId));
  const references = new Map(state.links.filter(l => l.noteId === noteId).map(l => [l.occurrenceId, l]));
  for (const run of state.runs) {
    if (noteId && run.noteId === noteId && !removed.has(run.occurrenceId) && !references.has(run.occurrenceId))
      references.set(run.occurrenceId, {nodeId: run.nodeId, occurrenceId: run.occurrenceId, day: run.scheduledDay});
  }
  if (includeDue) for (const r of state.routines) {
    const occurrence = nextOccurrence(r, state, dayKey(now, r.schedule?.zone));
    if (occurrence?.day && !occurrence.conflict && occurrence.day <= dayKey(now, r.schedule?.zone)
        && !removed.has(occurrence.id) && !references.has(occurrence.id))
      references.set(occurrence.id, {nodeId: r.nodeId, occurrenceId: occurrence.id, day: occurrence.day});
  }
  return [...references.values()].flatMap(ref => {
    const routine = state.routines.find(r => r.nodeId === ref.nodeId);
    if (!routine) return [];
    const runs = state.runs.filter(run => run.occurrenceId === ref.occurrenceId);
    return [{nodeId: ref.nodeId, noteId, routine, occurrence: {id: ref.occurrenceId, day: ref.day ?? null},
      executionId: JSON.stringify(['routine', ref.occurrenceId]),
      done: runs.some(run => run.state === 'done'), runIds: runs.map(run => run.runId),
      workedOn: [], pinned: false,
      order: state.positions.get(JSON.stringify([noteId, ref.occurrenceId]))?.order}];
  });
}

export function mergeTaskList(items, routines) {
  const end = items.reduce((max, item) => Math.max(max, item.order), -1) + 1;
  return [...items.map(item => ({...item, executionId: JSON.stringify(['task', item.nodeId])})),
    ...routines.map((item, i) => ({...item, order: item.order ?? end + i}))]
    .sort((a, b) => a.order - b.order || a.executionId.localeCompare(b.executionId));
}

/** Complete exactly the displayed occurrence, preserving time, notes and later repeats.
 * Undo only touches the sessions the caller saw, retaining unseen remote completion.
 */
export function setOccurrenceDone(dm, routine, occurrence, steps, noteId, done, observedRunIds = [], now = new Date()) {
  const state = getRoutineState(dm);
  const runs = state.runs.filter(run => run.occurrenceId === occurrence.id);
  const day = dayKey(now, routine.schedule?.zone);
  if (done && runs.some(run => run.state === 'done')) return;
  const selected = done ? runs.filter(run => run.state !== 'done') : runs.filter(run => observedRunIds.includes(run.runId) && run.state === 'done');
  // Validate all clocks before appending anything: Yjs transactions do not roll back.
  const values = new Map();
  for (const run of selected) for (const step of run.steps) {
    const result = run.results[step.id] || {};
    const seconds = result.runningAt ? Math.round((result.seconds || 0) + Math.max(0, now - Date.parse(result.runningAt)) / 1000) : result.seconds || 0;
    if (!Number.isFinite(seconds) || seconds > MAX_ROUTINE_SECONDS) throw Error(t('routines.error.reviewTimer'));
    values.set(JSON.stringify([run.runId, step.id]), {done, seconds, runningAt: null});
  }
  dm.doc.transact(() => {
    const creating = done && !selected.length;
    if (creating) {
      const started = startRoutine(dm, routine, occurrence, steps, noteId);
      selected.push({...started, runId: started.runId || started.id});
    }
    if (!creating && noteId && selected.length && !state.links.some(l => l.noteId === noteId && l.occurrenceId === occurrence.id))
      appendRoutineEvent(dm, {kind: 'link', nodeId: routine.nodeId, noteId, occurrenceId: occurrence.id, day: occurrence.day, title: routine.title, removed: false});
    for (const run of selected) {
      for (const step of run.steps) appendRoutineEvent(dm, {kind: 'result', runId: run.runId, stepId: step.id,
        values: values.get(JSON.stringify([run.runId, step.id])) || {done}});
      appendRoutineEvent(dm, {kind: 'finish', runId: run.runId, state: done ? 'done' : 'active', day});
    }
  }, USER_ORIGIN);
}
